// /finance's Team (finance redesign slice 3): who BeNerdy owes. Owed now, paid this year and last paid, every figure the
// server's (TD-102); which client each owed amount is for reads in the person's team invoices, not on the row. No strip,
// no search (J24). A row opens the person's drawer (`modal=person&person=`, finance-real, the user 2026-10-23: "opens a
// modal with all the invoices sent to the person"): what they are owed and their team invoices, each opening its own
// drawer. The row click is its only action, so it has no ⋮ (DD-045): Pay became the team invoice, and the statement the
// person's drawer. Add freelancer is the page header's (TD-106). Once the drawers a row opened are all closed, focus goes
// back to that row (useOpenerFocus).
import { TablePageBody } from "@/components/PageLayout/TablePageBody";
import { Badge } from "@/components/ui/Badge";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import { FilterBar } from "@/components/ui/FilterBar";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { TabPending } from "../components/TabPending";
import { TotalsFooter } from "../components/TotalsFooter";
import { useAgencyTeam } from "../hooks/useAgencyFinance";
import { useFinanceParams, type ParamPatch } from "../hooks/useFinanceParams";
import type { AgencyTeamPerson } from "../types";
import { dateLabel } from "../utils/format";
import { EXTERNAL } from "../utils/people";
import { useOpenerFocus } from "./useOpenerFocus";

const freelancerWord = ENTITY_CONFIG.freelancer.label.toLowerCase();

/** A row's click: the person's drawer, owed or not. */
export const personTarget = (p: AgencyTeamPerson): ParamPatch => ({ modal: "person", person: p.id });

const columns: DataColumn<AgencyTeamPerson>[] = [
  {
    key: "person", label: "Person",
    render: (p) => (
      <span className="flex flex-wrap items-center gap-x-2 font-medium text-gray-900">
        {p.name}
        {p.isOwner && <Badge tone="indigo">Owner</Badge>}
        {p.external && <Badge tone="gray">{EXTERNAL}</Badge>}
      </span>
    ),
  },
  {
    key: "owed", label: "Owed now", align: "right", width: "w-36",
    render: (p) => (
      <span className="flex flex-col items-end">
        <MoneyText amountMinor={p.owedMinor} strong={p.owedMinor > 0} balance />
        {p.aheadMinor > 0 && <span className="text-xs text-gray-500">{`Paid ahead ${formatMoney(p.aheadMinor, "EUR")}`}</span>}
      </span>
    ),
  },
  { key: "paid", label: "Paid this year", align: "right", width: "hidden sm:table-cell w-36", render: (p) => <MoneyText amountMinor={p.paidThisYearMinor} /> },
  { key: "last", label: "Last paid", width: "hidden sm:table-cell w-32 whitespace-nowrap", render: (p) => <span className="text-gray-600">{dateLabel(p.lastPaidOn)}</span> },
];

export function TeamTab({ onOpen }: { onOpen: (patch: ParamPatch) => void }) {
  const query = useAgencyTeam();
  const rememberOpener = useOpenerFocus(useFinanceParams().params.get("modal"));
  const openRow = (p: AgencyTeamPerson) => {
    rememberOpener();
    onOpen(personTarget(p));
  };
  const filters = <FilterBar selects={[]} trailing={<span className="text-xs text-gray-500">Click a person to see their invoices.</span>} />;
  // The note is the table's own toolbar, so it stays put while the table loads or fails, in its empty row.
  const empty = query.data ? (
    `No team yet. People join from Setup, or add a ${freelancerWord}.`
  ) : (
    <TabPending failed={query.isError} message="Could not load the team." retrying={query.isFetching} onRetry={() => void query.refetch()} />
  );
  return (
    <TablePageBody>
      <DataTable
        toolbar={filters}
        columns={columns}
        rows={query.data?.people ?? []}
        rowKey={(p) => p.id}
        onRowClick={openRow}
        empty={empty}
        footer={
          query.data && (
            <TotalsFooter
              items={[
                { label: "Owed now", amountMinor: query.data.totals.owedMinor },
                { label: "Paid this year", amountMinor: query.data.totals.paidThisYearMinor },
              ]}
            />
          )
        }
      />
    </TablePageBody>
  );
}
